<template>
  <FileDifMenu
    :project-param-id="projectParamId"
    :xsl-param-id="currentRequestId"
    :xml-param-id="xmlParamId"
    :diff-view="getType"
  />

  <div class="diff-container">
    <!-- Sidebar -->
    <div v-show="isSidebarVisible" class="sidebar" id="leftBar">
      <div class="sidebar-header d-flex align-items-center justify-content-between">
        <h5 class="fw-bold mb-0">XML Files</h5>
        <button class="btn btn-sm btn-light toggle-btn" @click="toggleSidebar">
          <i :class="isSidebarVisible ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"></i>
        </button>
      </div>

      <ul class="list-group mt-3 file-list">
        <li
          v-for="file in files"
          :key="file.xmlFileName"
          class="list-group-item text-truncate file-item"
          :class="{ active: file.xmlFileName === selectedFile.xmlFileName }"
          @click="selectFile(file)"
        >
          {{ file.xmlFileName }}
        </li>
      </ul>
    </div>

    <!-- Right Content Area -->
    <div class="content-area" id="rightBar">
      <div v-if="loading" class="process-loading">
        <div class="lds-roller">
          <div></div><div></div><div></div><div></div>
          <div></div><div></div><div></div><div></div>
        </div>
      </div>

      <div v-else class="diff-viewer">

        <!-- Toggle button when sidebar is hidden -->
        <button v-show="!isSidebarVisible"
          class="btn btn-sm btn-light toggle-btn float-start mt-2 ms-2"
          @click="toggleSidebar">
          <i :class="isSidebarVisible ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"></i>
        </button>

        <!-- File Upload -->
        <div class="file-upload-container mt-1 px-3">
          <input type="file" multiple @change="readFile()" ref="fileInput"
                 class="file-input" style="display:none;" accept=".xml" />

          <button @click="this.$refs.fileInput.click()" class="upload-btn">
            Select File
          </button>
        </div>

        <!-- CodeDiff -->
        <div class="code-diff-wrapper mt-3">
          <CodeDiff
            ref="diffScrollWrapper"
            :old-string="leftFile"
            :new-string="rightFile"
            output-format="side-by-side"
            style="height: 75vh; padding: 0px; overflow: auto"
            language="XML"
            :filename="leftFileName"
            :newFilename="rightFileName"
            class="col"
          />
        </div>

      </div>
    </div>
  </div>
</template>


<script>
import FileDifMenu from "../XmlandFileDifMenu.vue";
import { getCookie } from "../../store/cookie.js";
import { getSessionItem, setSessionItem } from "../../store/session.js";
import { type } from "@/utils/util";
import { makeOpearationRequest } from "@/services/apiServices";
import Split from "split.js";
import { pageViews } from "@/utils/util";

export default {
  name: "FileDifferenceNew",
  setup() {
    return {
      type,
      makeOpearationRequest,
    };
  },
  data() {
    return {
      currentRequestId: 0,
      files: [],
      selectedFile: {},
      loading: true,
      inputXML: "File is not Uploaded ",
      leftFile: "",
      rightFile: "",
      leftFileName: "",
      rightFileName: "",
      isSidebarVisible: true,
      offset: 0,
      limit: 1000,
      hasMore: true,
      diffChunks: [],
      lastScrollTop: 0,
      uploadedFileContent: null, // store uploaded file content for chunking
      uploadedFileName: null,
    };
  },
  components: {
    FileDifMenu,
    // CodeDiff,
  },
  mounted() {
    this.files = this.xmlRequestIds;
    const sessionSelectedFile = getSessionItem("xmlDiffLastSelectedFile");
    this.selectedFile = sessionSelectedFile || this.files[0];
    this.currentRequestId = this.files[0].xmlId;
    setSessionItem("currentXmlId", this.currentRequestId);
    this.resetAndLoadDiff();
    Split(["#leftBar", "#rightBar"], {
      sizes: [20, 80],
      minSize: [200, 400],
      gutterSize: 3,
      cursor: "col-resize",
    });
    this.$nextTick(() => {
      const diffScroller =
        this.$refs.diffScrollWrapper?.$el || this.$refs.diffScrollWrapper;
      if (diffScroller) {
        diffScroller.addEventListener("scroll", this.handleDiffScroll);
      }
    });
  },
  beforeUnmount() {
    const diffScroller =
      this.$refs.diffScrollWrapper?.$el || this.$refs.diffScrollWrapper;
    if (diffScroller) {
      diffScroller.removeEventListener("scroll", this.handleDiffScroll);
    }
  },
  computed: {
    getType() {
      return type.XML;
    },
    userId() {
      //return Store.getters.getUserId;
      return getCookie("userId");
    },
    xmlRequestIds() {
      return getSessionItem("xmlrequestId");
    },
  },
  methods: {
    // Infinite scroll logic
    handleDiffScroll() {
      const diffScroller =
        this.$refs.diffScrollWrapper?.$el || this.$refs.diffScrollWrapper;
      if (!diffScroller || this.loading || !this.hasMore) return;
      const threshold = 2;
      const isScrollingDown = diffScroller.scrollTop > this.lastScrollTop;
      const atEnd =
        diffScroller.scrollTop + diffScroller.clientHeight >=
        diffScroller.scrollHeight - threshold;
      if (isScrollingDown && atEnd) {
        this.loadMoreDiff();
      }
      this.lastScrollTop = diffScroller.scrollTop;
    },
    async loadMoreDiff() {
      this.loading = true;
      let data;
      // If an uploaded file is present, use it for leftFile and chunk rightFile from API
      if (this.uploadedFileContent) {
        // For uploaded file, left side is from uploaded content, right side from API
        // Only fetch right side from API
        data = await this.showDifference(this.offset, this.limit);
        if (data) {
          if (this.offset === 0) {
            this.leftFileName = this.uploadedFileName || "Uploaded File";
            this.rightFileName = "Transformed File";
          }
          const leftLines = (this.uploadedFileContent || "").split("\n");
          const rightLines = (data.transformedXml || "").split("\n");
          const leftChunk = leftLines.slice(
            this.offset,
            this.offset + this.limit
          );
          const rightChunk = rightLines.slice(
            this.offset,
            this.offset + this.limit
          );

          if (this.offset === 0) {
            this.leftFile = leftChunk.join("\n");
            this.rightFile = rightChunk.join("\n");
          } else {
            this.leftFile += "\n" + leftChunk.join("\n");
            this.rightFile += "\n" + rightChunk.join("\n");
          }

          this.offset += this.limit;
          const moreLeft = leftLines.length > this.offset;
          const moreRight = rightLines.length > this.offset;
          this.hasMore = moreLeft || moreRight;
        } else {
          this.hasMore = false;
        }
        this.loading = false;
        return;
      }
      // ...existing code for API-based diff...
      data = await this.showDifference(this.offset, this.limit);
      if (data) {
        if (this.offset === 0) {
          this.leftFileName = "Source File";
          this.rightFileName = "Transformed File";
        }
        const leftLines = (data.inputXml || "").split("\n");
        const rightLines = (data.transformedXml || "").split("\n");
        const leftChunk = leftLines.slice(0, this.limit);
        const rightChunk = rightLines.slice(0, this.limit);

        if (this.offset === 0) {
          this.leftFile = leftChunk.join("\n");
          this.rightFile = rightChunk.join("\n");
        } else {
          this.leftFile += "\n" + leftChunk.join("\n");
          this.rightFile += "\n" + rightChunk.join("\n");
        }

        this.offset += this.limit;
        const moreLeft = leftLines.length >= this.limit;
        const moreRight = rightLines.length >= this.limit;
        this.hasMore = moreLeft || moreRight;
      } else {
        this.hasMore = false;
      }
      this.loading = false;
    },
    resetAndLoadDiff() {
      this.offset = 0;
      this.hasMore = true;
      this.diffChunks = [];
      this.leftFile = "";
      this.rightFile = "";
      this.leftFileName = "";
      this.rightFileName = "";
      this.loading = true;
      this.uploadedFileContent = null;
      this.uploadedFileName = null;
      this.loadMoreDiff();
    },
    async showDifference(offset = 0, limit = 1000) {
      var body = {
        userId: this.userId,
        type: type.XML,
        requestId: this.currentRequestId,
        offset,
        limit,
        chunked: true,
      };
      const endPoint = "/compare";
      try {
        const response = await this.makeOpearationRequest(
          this.$acclConfigs,
          endPoint,
          body
        );
        console.log("Response", response);
        if (response.status === 200) {
          return response.data;
        } else {
          console.log("Failure in File Comparision :", response.data.message);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },
    invokeShowDifference() {
      this.showDifference()
        .then((data) => {
          if (data) {
            this.leftFile = "Please upload a file";
            this.leftFileName = "Source File";
            this.rightFile = data.transformedXml ? data.transformedXml : "";
            this.rightFileName = "Transformed File";
          } else {
            console.log("No data received from showDifference");
          }
        })
        .catch((error) => {
          console.error("Error in invokeShowDifference:", error);
        });
    },

    readFile() {
      const file = this.$refs.fileInput.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          this.uploadedFileContent = e.target.result;
          this.uploadedFileName = file.name;
          this.offset = 0;
          this.hasMore = true;
          this.diffChunks = [];
          this.leftFile = "";
          this.rightFile = "";
          this.leftFileName = "";
          this.rightFileName = "";
          this.loading = true;
          // Start infinite scroll loading for uploaded file
          this.loadMoreDiff();
        };
        reader.readAsText(file);
      }
    },
    async showDifference() {
      var body = {
        userId: this.userId,
        type: type.XML,
        requestId: this.currentRequestId,
      };
      const endPoint = "/compare";
      try {
        const response = await this.makeOpearationRequest(
          this.$acclConfigs,
          endPoint,
          body
        );
        console.log("Response", response);
        if (response.status === 200) {
          return response.data;
        } else {
          console.log("Failure in File Comparision :", response.data.message);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },
    toggleSidebar() {
      this.isSidebarVisible = !this.isSidebarVisible;
    },
    selectFile(file) {
      this.selectedFile = file;
      setSessionItem("xmlDiffLastSelectedFile", file);
      this.selectedFile = file;
      this.currentRequestId = file.xmlId;
      setSessionItem("currentXmlId", this.currentRequestId);
      this.resetAndLoadDiff();
    },
  },
  props: ["projectParamId"],
};
</script>

<style scoped>

.diff-container {
  display: flex;
  height: calc(100vh - 150px);
  border: 1px solid var(--border-color);
  overflow: hidden;
  background: var(--surface);
}

/* Sidebar */
.sidebar {
  width: 250px;
  background: var(--surface-hover);
  border-right: 1px solid var(--border-color);
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  padding: 1rem 0.75rem;
}

.sidebar-header {
  position: sticky;
  top: 0;
  background: var(--surface-hover);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
  z-index: 2;
}

/* File list with left scrollbar */
.file-list {
  direction: rtl;
  text-align: left;
  overflow-y: auto;
  max-height: calc(75vh - 50px);
  padding: 6px 4px;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.file-list::-webkit-scrollbar {
  width: 2px;
}
.file-list::-webkit-scrollbar-thumb {
  background: var(--primary);
}
.file-list::-webkit-scrollbar-thumb:hover {
  background: var(--primary-hover);
}



/* File items */
.file-item {
  direction: ltr;
  font-size: .9rem;
  cursor: pointer;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 20px 14px;
  background: var(--surface);
  display: flex;
  align-items: center;
}
.file-item:hover {
  background: var(--surface-hover);
}
.file-item.active {
  background: var(--primary);
  color: white;
  border-color: transparent;
}

/* Right container */
.content-area {
  flex-grow: 1;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  /* overflow: auto; */
}

.code-diff-wrapper {
  height: 100%;
  /* overflow: auto; */
  border-left: 1px solid var(--border-color);
  background: var(--surface-hover);
}

/* Upload button */
.upload-btn {
  border: none;
  justify-content: end;
  align-items: end;
  right: 0;
  background: var(--primary);
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  right: 0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
}

 .file-upload-container{
   width: 100%;
  display: flex;
} 

/* Spinner */
.process-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

/* Tiny toggle button */
.toggle-btn {
  border: none;
  background: transparent;
  color: var(--text-secondary);
}
.toggle-btn:hover {
  color: var(--primary);
}

</style>
